import type { SheetsClient } from '../client';
import type { TransactionRow } from '../types';
import {
  appendRow,
  genericSoftDelete,
  genericUpdate,
  newRowDefaults,
  readAllRows,
  type ListOptions,
} from './_base';
import { parseBool, parseInt10 } from '../row-utils';

const TAB = 'transactions' as const;

function parse(o: Record<string, string>): TransactionRow {
  return {
    id: o['id'] ?? '',
    book_id: o['book_id'] ?? '',
    date: o['date'] ?? '',
    amount_minor: parseInt10(o['amount_minor'], 0),
    currency: o['currency'] ?? 'TWD',
    kind: ((o['kind'] ?? 'expense') as TransactionRow['kind']),
    category_id: o['category_id'] ?? '',
    subcategory_id: o['subcategory_id'] ?? '',
    note: o['note'] ?? '',
    project: o['project'] ?? '',
    tags: o['tags'] ?? '',
    recurring_id: o['recurring_id'] ?? '',
    rev: parseInt10(o['rev'], 0),
    created_at: o['created_at'] ?? '',
    updated_at: o['updated_at'] ?? '',
    deleted_at: (o['deleted_at'] ?? '') as TransactionRow['deleted_at'],
  };
}
void parseBool;

export const transactionsRepo = {
  async list(
    client: SheetsClient,
    spreadsheetId: string,
    options?: ListOptions,
  ): Promise<TransactionRow[]> {
    const rows = await readAllRows(client, spreadsheetId, TAB, options);
    const out: TransactionRow[] = [];
    for (const r of rows) {
      try {
        out.push(parse(r));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Skipping malformed transaction row', { id: r['id'], err });
      }
    }
    return out;
  },

  async get(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
  ): Promise<TransactionRow | null> {
    const all = await readAllRows(client, spreadsheetId, TAB, { includeDeleted: true });
    const row = all.find((r) => r['id'] === id);
    return row ? parse(row) : null;
  },

  async create(
    client: SheetsClient,
    spreadsheetId: string,
    input: Partial<TransactionRow> & { id?: string },
  ): Promise<TransactionRow> {
    const row = {
      ...newRowDefaults({
        id: input.id,
        book_id: input.book_id ?? '',
        date: input.date ?? '',
        amount_minor: input.amount_minor ?? 0,
        currency: input.currency ?? 'TWD',
        kind: input.kind ?? 'expense',
        category_id: input.category_id ?? '',
        subcategory_id: input.subcategory_id ?? '',
        note: input.note ?? '',
        project: input.project ?? '',
        tags: input.tags ?? '',
        recurring_id: input.recurring_id ?? '',
      }),
    };
    await appendRow(client, spreadsheetId, TAB, row);
    return parse(row);
  },

  update(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    patch: Partial<TransactionRow>,
    expectedRev: number,
  ) {
    return genericUpdate(client, spreadsheetId, TAB, id, patch as Record<string, unknown>, expectedRev);
  },

  softDelete(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    expectedRev: number,
  ) {
    return genericSoftDelete(client, spreadsheetId, TAB, id, expectedRev);
  },
};
